import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Building,
  MapPin,
  Search,
  Layers,
  Ruler,
  BarChart2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Star,
} from 'lucide-react';

import { AppHeader, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const GRB_WMS_URL = 'https://geo.api.vlaanderen.be/GRB/wms';
const GRB_ATTRIBUTION = '© Digitaal Vlaanderen - GRB';

const formatArea = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 'Onbekend';
  }
  return `${Math.round(value).toLocaleString('nl-BE')} m²`;
};

const formatFlag = (flag, fallbackBool) => {
  if (typeof flag === 'string' && flag.trim()) {
    return flag.trim().toLowerCase();
  }
  if (fallbackBool === true) return 'ja';
  if (fallbackBool === false) return 'nee';
  return 'onbekend';
};

const USER_STATUS_OPTIONS = [
  /*{ value: 'favorieten', label: 'Favorieten' },*/
  { value: 'new', label: 'Nieuw' },
  { value: 'gelezen', label: 'Gelezen' },

];

const STATUS_LABELS = {
  new: 'Nieuw',
  favorieten: 'Favorieten',
  gelezen: 'Gelezen',
};

const getStatusLabel = (status) => STATUS_LABELS[status] || STATUS_LABELS.new;

const LeafletMap = ({ geometry }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const geoLayerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return () => {};
    }

    if (!mapRef.current) {
      const map = L.map(container, {
        zoomControl: false,
        attributionControl: false,
      });

      L.control.attribution({ prefix: false }).addTo(map).setPrefix(GRB_ATTRIBUTION);

      L.tileLayer.wms(GRB_WMS_URL, {
        layers: 'GRB_BSK',
        format: 'image/png',
        transparent: false,
        version: '1.3.0',
      }).addTo(map);

      L.tileLayer.wms(GRB_WMS_URL, {
        layers: 'GRB_ADP_GRENS',
        format: 'image/png',
        transparent: true,
        version: '1.3.0',
      }).addTo(map);

      mapRef.current = map;
    } else {
      mapRef.current.invalidateSize();
    }

    return () => {
      if (!mapRef.current) {
        return;
      }
      mapRef.current.remove();
      mapRef.current = null;
      geoLayerRef.current = null;
      if (container) {
        container._leaflet_id = undefined;
        container.innerHTML = '';
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const map = mapRef.current;

    if (!geoLayerRef.current) {
      geoLayerRef.current = L.geoJSON(null, {
        style: {
          color: '#047857',
          weight: 2,
          fillColor: '#10b981',
          fillOpacity: 0.25,
        },
        pointToLayer: (_feature, latlng) =>
          L.circleMarker(latlng, {
            radius: 8,
            color: '#047857',
            weight: 2,
            fillColor: '#10b981',
            fillOpacity: 0.7,
          }),
      }).addTo(map);
    }

    const layer = geoLayerRef.current;
    layer.clearLayers();

    if (!geometry) {
      map.invalidateSize();
      return;
    }

    layer.addData(geometry);

    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [16, 16] });
    } else if (geometry?.type === 'Point') {
      const [lon, lat] = geometry.coordinates;
      map.setView([lat, lon], 18);
    }

    layer.bringToFront();
    map.invalidateSize();
  }, [geometry]);

  if (!geometry) {
    return (
      <div className="h-full w-full flex items-center justify-center text-sm text-gray-500">
        Geen locatie beschikbaar.
      </div>
    );
  }

  return <div ref={containerRef} className="h-full w-full" />;
};

const VacantSpacesModule = () => {
  const { user, logout, token } = useContext(AuthContext);
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 500;

  const [selectedProvince, setSelectedProvince] = useState('all');
  const [selectedGemeente, setSelectedGemeente] = useState('all');
  const [selectedPostcode, setSelectedPostcode] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatsTab, setActiveStatsTab] = useState(null); // 'total', 'leegstand', 'verwaarloosd', 'favorieten', or null

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [statusValue, setStatusValue] = useState('new');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [favoriteUpdating, setFavoriteUpdating] = useState(false);
  const [favoriteTargetId, setFavoriteTargetId] = useState(null);

  // Use stats directly from backend - always shows total counts
  // Do NOT recalculate from filtered listings

  const buildFilterParams = (withPagination = false) => {
    const params = new URLSearchParams();
    if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
    if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
    if (selectedPostcode) params.set('postcode', selectedPostcode);
    
    // Apply filter based on activeStatsTab if set, otherwise use statusFilter
    if (activeStatsTab === 'leegstand') {
      params.set('status', 'vacant');
    } else if (activeStatsTab === 'verwaarloosd') {
      params.set('status', 'neglected');
    } else if (activeStatsTab === 'favorieten') {
      params.set('favorites_only', 'true');
      // Don't apply statusFilter when favorites_only is active
    } else if (statusFilter && statusFilter !== 'all') {
      params.set('status', statusFilter);
    }
    
    if (userStatusFilter && userStatusFilter !== 'all') params.set('user_status', userStatusFilter);
    if (searchTerm) params.set('search', searchTerm);
    if (withPagination) {
      params.set('skip', (currentPage * PAGE_SIZE).toString());
      params.set('limit', PAGE_SIZE.toString());
    }
    return params;
  };

  const fetchStats = async () => {
    if (!token) return;
    try {
      // Build params WITHOUT activeStatsTab filter - stats should always show total counts
      const params = new URLSearchParams();
      if (selectedProvince && selectedProvince !== 'all') params.set('province', selectedProvince);
      if (selectedGemeente && selectedGemeente !== 'all') params.set('gemeente', selectedGemeente);
      if (selectedPostcode) params.set('postcode', selectedPostcode);
      // Note: Do NOT apply activeStatsTab or statusFilter to stats - stats should show totals
      if (userStatusFilter && userStatusFilter !== 'all') params.set('user_status', userStatusFilter);
      if (searchTerm) params.set('search', searchTerm);
      
      const query = params.toString();
      const response = await axios.get(`${API}/vacant-spaces/stats${query ? `?${query}` : ''}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchListings = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = buildFilterParams(true);
      const url = `${API}/vacant-spaces/listings?${params.toString()}`;
      console.log('Fetching listings with params:', url); // Debug log
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setListings(items);
      if (typeof response.data?.total === 'number') {
        setTotalCount(response.data.total);
      } else {
        setTotalCount(items.length);
      }
    } catch (err) {
      console.error('Failed to fetch listings:', err);
      setListings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchStats();
    // Note: activeStatsTab is NOT in dependencies - stats should always show totals
  }, [token, selectedProvince, selectedGemeente, selectedPostcode, userStatusFilter, searchTerm]);

  useEffect(() => {
    fetchListings();
  }, [token, selectedProvince, selectedGemeente, selectedPostcode, statusFilter, userStatusFilter, searchTerm, currentPage, activeStatsTab]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedProvince, selectedGemeente, selectedPostcode, statusFilter, userStatusFilter, searchTerm, activeStatsTab]);

  const provinces = useMemo(() => {
    if (!stats) {
      return [
        { value: 'all', label: 'Alle provincies' },
        { value: 'Antwerpen', label: 'Antwerpen' },
        { value: 'Limburg', label: 'Limburg' },
        { value: 'Oost-Vlaanderen', label: 'Oost-Vlaanderen' },
        { value: 'Vlaams-Brabant', label: 'Vlaams-Brabant' },
        { value: 'West-Vlaanderen', label: 'West-Vlaanderen' },
      ];
    }
    return [{ value: 'all', label: 'Alle provincies' }, ...(stats.provinces || []).map((name) => ({ value: name, label: name }))];
  }, [stats]);

  const gemeenten = useMemo(() => {
    if (!stats) {
      return [];
    }
    return (stats.municipalities || []).map((name) => ({ value: name, label: name }));
  }, [stats]);

  const handleNavigate = (path) => {
    window.location.href = path;
  };

  const geometryFeature = useMemo(() => {
    if (!selectedListing?.geometry) {
      return null;
    }
    return selectedListing.geometry;
  }, [selectedListing]);

  const applyListingUpdate = (updatedListing) => {
    if (!updatedListing) return;
    setListings((prev) => prev.map((item) => (item.id === updatedListing.id ? { ...item, ...updatedListing } : item)));
    setSelectedListing((prev) => (prev && prev.id === updatedListing.id ? { ...prev, ...updatedListing } : prev));
  };

  const patchListingUserData = async (listingId, payload) => {
    if (!token) return null;
    const response = await axios.patch(`${API}/vacant-spaces/listings/${listingId}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const updated = response.data;
    applyListingUpdate(updated);
    await fetchStats();
    return updated;
  };

  const handleViewDetails = useCallback(
    (listing) => {
      if (!listing) return;
      setSelectedListing(listing);
      setStatusValue(listing.status || 'new');
      setSheetOpen(true);

      if ((listing.status || 'new') !== 'gelezen') {
        patchListingUserData(listing.id, { status: 'gelezen' }).catch((err) =>
          console.error('Failed to auto-mark as gelezen:', err),
        );
      }
    },
    [patchListingUserData],
  );

  const handleToggleFavorite = async (listing, nextValue) => {
    if (!listing) return;
    setFavoriteUpdating(true);
    setFavoriteTargetId(listing.id);
    try {
      await patchListingUserData(listing.id, { is_favorite: nextValue });
    } catch (err) {
      console.error('Failed to update favorite:', err);
    } finally {
      setFavoriteUpdating(false);
      setFavoriteTargetId(null);
    }
  };

  const handleDrawerStatusSave = async () => {
    if (!selectedListing) return;
    if ((selectedListing.status || 'new') === statusValue) {
      return;
    }
    setStatusUpdating(true);
    try {
      const updated = await patchListingUserData(selectedListing.id, { status: statusValue });
      if (updated?.status) {
        setStatusValue(updated.status);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  useEffect(() => {
    if (!selectedListing) {
      setStatusValue('new');
      return;
    }
    setStatusValue(selectedListing.status || 'new');
  }, [selectedListing]);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader
        user={user}
        currentPage="vacant-spaces"
        onNavigate={handleNavigate}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Leegstaande bedrijfsruimten</h2>
          <p className="text-gray-600">Mercator dataset met leegstand en verwaarlozing</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'total' ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => {
                setActiveStatsTab(activeStatsTab === 'total' ? null : 'total');
                setStatusFilter('all');
                setSelectedProvince('all');
                setSelectedGemeente('all');
                setSelectedPostcode('');
                setUserStatusFilter('all');
                setSearchTerm('');
              }}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Building className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Totaal dossiers</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'leegstand' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => {
                setActiveStatsTab(activeStatsTab === 'leegstand' ? null : 'leegstand');
                setStatusFilter('vacant');
                setSelectedProvince('all');
                setSelectedGemeente('all');
                setSelectedPostcode('');
                setUserStatusFilter('all');
                setSearchTerm('');
              }}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Layers className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Leegstaand</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.vacant}</p>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'verwaarloosd' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => {
                setActiveStatsTab(activeStatsTab === 'verwaarloosd' ? null : 'verwaarloosd');
                setStatusFilter('neglected');
                setSelectedProvince('all');
                setSelectedGemeente('all');
                setSelectedPostcode('');
                setUserStatusFilter('all');
                setSearchTerm('');
              }}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <BarChart2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Verwaarloosd</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.neglected}</p>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'favorieten' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => {
                setActiveStatsTab(activeStatsTab === 'favorieten' ? null : 'favorieten');
                setStatusFilter('all');
                setSelectedProvince('all');
                setSelectedGemeente('all');
                setSelectedPostcode('');
                setUserStatusFilter('all');
                setSearchTerm('');
              }}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Favorieten</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.favorites_count || 0}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="min-w-60">
                <Label className="sr-only">Provincie</Label>
                <Select value={selectedProvince} onValueChange={setSelectedProvince}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies provincie" />
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((province) => (
                      <SelectItem key={province.value} value={province.value}>
                        {province.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-64">
                <Label className="sr-only">Gemeente</Label>
                <Select value={selectedGemeente} onValueChange={setSelectedGemeente}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies gemeente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle gemeenten</SelectItem>
                    {gemeenten.map((gemeente) => (
                      <SelectItem key={gemeente.value} value={gemeente.value}>
                        {gemeente.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-48">
                <Label className="sr-only">Postcode</Label>
                <Input
                  type="text"
                  placeholder="Postcode"
                  value={selectedPostcode}
                  onChange={(e) => setSelectedPostcode(e.target.value)}
                />
              </div>

              <div className="min-w-48">
                <Label className="sr-only">Status</Label>
                <Select value={statusFilter} onValueChange={(value) => {
                  setStatusFilter(value);
                  setActiveStatsTab(null); // Clear active stats tab when manually changing status filter
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="vacant">Leegstand</SelectItem>
                    <SelectItem value="neglected">Verwaarloosd</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-48">
                <Label className="sr-only">Gebruikersstatus</Label>
                <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies gebruikersstatus" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    {USER_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Zoek op gemeente, straat of capakey..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Resultaten</CardTitle>
            <div className="text-sm text-gray-600">
              {loading ? (
                'Laden...'
              ) : totalCount > 0 ? (
                (() => {
                  const startIndex = currentPage * PAGE_SIZE + 1;
                  const endIndex = Math.min((currentPage + 1) * PAGE_SIZE, totalCount);
                  return `Resultaten ${startIndex}-${endIndex} van ${totalCount} totaal`;
                })()
              ) : (
                'Geen resultaten'
              )}
            </div>
          </CardHeader>
          <CardContent>
            {listings.length === 0 && !loading ? (
              <div className="text-center py-8 text-gray-500">
                Geen resultaten gevonden
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {listings.map((item) => {
                    const leegstandFlag = formatFlag(item.leegstand_flag, item.is_vacant);
                    const verwaarlozingFlag = formatFlag(item.verwaarlozing_flag, item.is_neglected);
                    const showLeegstandBadge = leegstandFlag?.toLowerCase() === 'ja';
                    const showVerwaarlozingBadge = verwaarlozingFlag?.toLowerCase() === 'ja';
                    const statusLabel = getStatusLabel(item.status);
                    const statusBadgeClass =
                      item.status === 'gelezen'
                        ? 'bg-slate-100 text-slate-700 border-transparent'
                        : item.status === 'favorieten'
                          ? 'bg-amber-100 text-amber-700 border-transparent'
                          : 'bg-gray-100 text-gray-700 border-transparent';
                    return (
                      <div
                        onClick={() => handleViewDetails(item)}
                        key={item.id}
                        className="border border-gray-200 rounded-lg p-4 flex flex-col gap-4 hover:border-emerald-300 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>

                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-emerald-600" />
                              <h3 className="font-semibold text-gray-900">
                                {item.dossiernummer}-{item.volgnummer}
                              </h3>
                              <Badge className={statusBadgeClass}>{statusLabel}</Badge>
                              {showVerwaarlozingBadge && (
                                <Badge className="bg-red-100 text-red-700 border-transparent">Verwaarloosd</Badge>
                              )}
                              {showLeegstandBadge && (
                                <Badge className="bg-emerald-100 text-emerald-700 border-transparent">Leegstaand</Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span>
                                {item.straatnaam || 'Onbekende straat'} {item.huisnummer || ''}, {item.postcode || '????'} {item.gemeentenaam || ''}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-600">
                              <span>Perceel: {formatArea(item.parcel_area_m2)}</span>
                              <span>Gebouw: {formatArea(item.building_area_m2)}</span>
                              <span>Leegstand: {leegstandFlag}</span>
                              <span>Verwaarlozing: {verwaarlozingFlag}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                     
                            </div>
                            <div className="flex items-center gap-2">
                            <Button                          
                                variant="ghost"
                                size="icon"
                                className={`relative top-3 right-3 p-1 h-8 w-8 rounded-full hover:bg-amber-50 ${
                                  item.is_favorite ? 'text-amber-500' : 'text-gray-400'
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFavorite(item, !item.is_favorite);
                                }}
                                disabled={favoriteUpdating && favoriteTargetId === item.id}
                              >

                                <Star 
                                style={{ width: '30px', height: '30px' }}
                                className={`w-5 h-5 ${item.is_favorite  ? 'text-yellow-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} />                              
                             
                              </Button>


                              {/*<Button variant="outline" size="sm" className="flex items-center gap-2" onClick={() => handleViewDetails(item)}>
                                <Eye className="w-4 h-4" />
                                Bekijk
                              </Button>*/}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalCount > 0 && (
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
                    <div className="text-sm text-gray-600">
                      {(() => {
                        const startIndex = currentPage * PAGE_SIZE + 1;
                        const endIndex = Math.min((currentPage + 1) * PAGE_SIZE, totalCount);
                        return `Resultaten ${startIndex}-${endIndex} van ${totalCount}`;
                      })()}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                        disabled={currentPage === 0 || loading}
                        className="flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Vorige
                      </Button>
                      <div className="text-sm text-gray-600 min-w-[120px] text-center">
                        {(() => {
                          const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                          return `Pagina ${Math.min(currentPage + 1, totalPages)} van ${totalPages}`;
                        })()}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                          setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
                        }}
                        disabled={(() => {
                          const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                          return currentPage >= totalPages - 1 || loading;
                        })()}
                        className="flex items-center gap-1"
                      >
                        Volgende
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedListing && (
            <div className="space-y-6">
              <SheetHeader>
                <SheetTitle>Dossier {selectedListing.dossiernummer}</SheetTitle>

                  <Button
                    variant="ghost"
                    size="sm"
                    className={` p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${
                      selectedListing.is_favorite  ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                    }`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(selectedListing, !selectedListing.is_favorite );                        
                    }}
                    >
                    <Star className={`w-5 h-5 ${selectedListing.is_favorite  ? 'text-yellow-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px',marginRight: '10px' }} />                              
                  </Button>
                <SheetDescription>Volgnummer {selectedListing.volgnummer}</SheetDescription>
              </SheetHeader>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {selectedListing.straatnaam || 'Onbekende straat'} {selectedListing.huisnummer || ''}, {selectedListing.postcode || '????'}{' '}
                    {selectedListing.gemeentenaam || ''}
                  </span>
                </div>
                {selectedListing.provincie && <Badge variant="outline">{selectedListing.provincie}</Badge>}
              </div>

              {/*<div className="border border-gray-200 rounded-lg p-3 space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Select value={statusValue} onValueChange={setStatusValue}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Kies status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new" disabled>
                        Nieuw (automatisch)
                      </SelectItem>
                      {USER_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleDrawerStatusSave}
                    disabled={
                      statusUpdating || !selectedListing || (selectedListing.status || 'new') === statusValue
                    }
                    className="mt-3"
                  >
                    {statusUpdating ? 'Opslaan...' : 'Status opslaan'}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant={selectedListing?.is_favorite ? 'default' : 'outline'}
                  onClick={() => handleToggleFavorite(selectedListing, !selectedListing?.is_favorite)}
                  disabled={favoriteUpdating}
                  className="flex items-center gap-2"
                >
                  <Star
                    className="w-4 h-4"
                    fill={selectedListing?.is_favorite ? 'currentColor' : 'none'}
                  />
                  {selectedListing?.is_favorite ? 'Verwijder favoriet' : 'Markeer als favoriet'}
                </Button>
              </div>*/}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Perceeloppervlakte</p>
                  <p className="text-lg font-semibold text-gray-900">{formatArea(selectedListing.parcel_area_m2)}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Gebouwoppervlakte</p>
                  <p className="text-lg font-semibold text-gray-900">{formatArea(selectedListing.building_area_m2)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Leegstand</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {formatFlag(selectedListing.leegstand_flag, selectedListing.is_vacant)}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Verwaarlozing</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize">
                    {formatFlag(selectedListing.verwaarlozing_flag, selectedListing.is_neglected)}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-lg">Locatiekaart</h3>
                </div>
                <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200">
                  <LeafletMap geometry={geometryFeature} />
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default VacantSpacesModule;
